import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Users, Target, Heart, Award, Lightbulb, Rocket } from 'lucide-react';

// OPTION A: honest stats if you want to keep this section
const stats = [
  { number: '5+', label: 'Years of E-commerce Engineering' },
  { number: 'B2B & B2C', label: 'Platform Experience' },
  { number: 'HCL Commerce', label: 'Enterprise Platform Expertise' },
  { number: '100%', label: 'Direct Access to Your Engineer' },
];

const values = [
  { icon: Lightbulb, title: 'Innovation', description: 'Pushing boundaries with modern commerce technology' },
  { icon: Heart, title: 'Client-Centric', description: 'Your success is our priority' },
  { icon: Award, title: 'Excellence', description: 'Delivering quality in every project' },
  { icon: Rocket, title: 'Agility', description: 'Fast delivery without compromising quality' },
];

export default function AboutPage() {
  return (
    <>
      <Helmet>
        <title>About Us - FuturePath Technologies</title>
        <meta name="description" content="FuturePath Technologies specializes in e-commerce engineering — HCL Commerce, MedusaJS, and headless platform migrations for B2B and B2C brands." />
      </Helmet>

      <section className="relative min-h-[50vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="about-hero-title">
              About FuturePath
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              We specialize in e-commerce engineering — building and scaling commerce platforms for B2B and B2C brands.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="our-story-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-6 text-white">
                Our Story
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                FuturePath Technologies was founded on a simple premise: most agencies can build a website, but very few can handle the complexity of real B2B and B2C commerce platforms — pricing logic, catalog scale, enterprise integrations, and platform migrations that can't afford downtime.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                Our founder has spent 5+ years leading engineering on production e-commerce platforms, including migrating storefronts from legacy systems to headless HCL Commerce, and building on modern stacks like MedusaJS and Next.js.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                We work directly with clients as senior engineers — not through layers of account managers — so you get technical depth and fast decisions on every project.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=800"
                alt="E-commerce platform development"
                className="rounded-3xl glass-card p-2"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-surface/30" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="text-5xl md:text-6xl font-bold font-outfit text-primary mb-2">
                  {stat.number}
                </div>
                <div className="text-muted-foreground text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-outfit mb-4 text-white">
              Our Values
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-8 rounded-3xl text-center"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6">
                  <value.icon size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold font-outfit text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}